import React from "react";
import LazyImage from "web/react/components/lazy-load-image/lazy-load-image";
import { Button } from "web/react/emo/button";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view/view";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./lyst-news-article-card.css";

interface LystNewsArticleCardProps {
    imgSrc: string;
    title: string;
    url?: string;
    subtitle?: string;
    lazy?: boolean;
    alt?: string;
    isHero?: boolean;
}

function getImage(
    imgSrc: string,
    lazy?: boolean,
    alt?: string,
    isHero?: boolean,
    url?: string
): React.ReactElement {
    const imgContainerClassName = isHero ? styles.heroArticleImageContainer : styles.articleImage;
    const image = lazy ? (
        <LazyImage
            dataTestId="introimage"
            className={styles.heroArticleImage}
            src={imgSrc}
            alt={alt}
        />
    ) : (
        <img data-testid="introimage" className={styles.heroArticleImage} src={imgSrc} alt={alt} />
    );

    return url ? (
        <a className={imgContainerClassName} href={url}>
            {image}
        </a>
    ) : (
        <div className={imgContainerClassName}>{image}</div>
    );
}

function getHeading(
    title: string,
    subtitle?: string,
    isHero?: boolean,
    url?: string
): React.ReactElement {
    const heading = (
        <View marginBottom="md">
            <View marginBottom="xxs">{subtitle && <Text textStyle="body">{subtitle}</Text>}</View>
            <Heading
                as={isHero ? "h2" : "h3"}
                textStyle={isHero ? { xl: "hero-xl", lg: "hero-l", sm: "hero" } : "hero-xs"}
            >
                {title}
            </Heading>
        </View>
    );

    return url ? (
        <div>
            <a href={url}>{heading}</a>
            <Button
                title={gettext("general.read_more")}
                href={url}
                width="fit-content"
                variant={isHero ? "primary" : "secondary"}
            />
        </div>
    ) : (
        <div>{heading}</div>
    );
}

export function LystNewsArticleCard({
    imgSrc,
    title,
    url,
    subtitle,
    lazy,
    alt,
    isHero = false,
}: LystNewsArticleCardProps): React.ReactElement {
    return (
        <div data-testid="article-card">
            <View width="fit-content" data-testid="article-card">
                <div className={isHero ? styles.heroArticleCard : styles.articleCard}>
                    {getImage(imgSrc, lazy, alt, isHero, url)}
                    {getHeading(title, subtitle, isHero, url)}
                </div>
            </View>
        </div>
    );
}
